import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { youtubeApi, tiktokApi } from '../utils/api';
import { Video, VideoStatus, WebSocketMessage } from '../types';

const s = {
  label: { fontSize: 12, color: '#888', marginBottom: 6 } as React.CSSProperties,
  input: {
    flex: 1, minWidth: 0, padding: '7px 10px', fontSize: 13, border: '1px solid #e5e5e5',
    borderRadius: 5, color: '#111', outline: 'none',
  } as React.CSSProperties,
  btn: (primary?: boolean, danger?: boolean, disabled?: boolean) => ({
    padding: '7px 14px', fontSize: 13, borderRadius: 5,
    border: primary || danger ? 'none' : '1px solid #e5e5e5',
    background: danger ? '#fee2e2' : primary ? '#6441a5' : '#fff',
    color: danger ? '#b91c1c' : primary ? '#fff' : '#555',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    whiteSpace: 'nowrap',
  } as React.CSSProperties),
  row: { display: 'flex', gap: 8, alignItems: 'center' } as React.CSSProperties,
  section: { marginBottom: 24 } as React.CSSProperties,
  meta: { fontSize: 12, color: '#aaa' } as React.CSSProperties,
  videoRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 0', borderBottom: '1px solid #f0f0f0',
  } as React.CSSProperties,
  idx: {
    minWidth: 20, textAlign: 'center' as const, fontSize: 12, color: '#aaa',
  },
};

interface QueueManagementProps {
  channelId: number;
  lastMessage: WebSocketMessage | null;
}

const QueueManagement: React.FC<QueueManagementProps> = ({
  channelId,
  lastMessage
}) => {
  const [queue, setQueue] = useState<Video[]>([]);
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);
  const [addUrl, setAddUrl] = useState('');
  const [lastProcessedMessage, setLastProcessedMessage] = useState<WebSocketMessage | null>(null);
  const queryClient = useQueryClient();

  // Fetch initial queue data from both integrations
  const { data: ytQueueData, isLoading: ytLoading } = useQuery({
    queryKey: ['yt-queue', channelId],
    queryFn: () => youtubeApi.getQueue(channelId).then(res => res.data),
  });

  const { data: ttQueueData, isLoading: ttLoading } = useQuery({
    queryKey: ['tt-queue', channelId],
    queryFn: () => tiktokApi.getQueue(channelId).then(res => res.data),
  });

  const { data: ytCurrentData } = useQuery({
    queryKey: ['yt-current', channelId],
    queryFn: () => youtubeApi.getCurrentVideo(channelId).then(res => res.data),
  });

  const { data: ttCurrentData } = useQuery({
    queryKey: ['tt-current', channelId],
    queryFn: () => tiktokApi.getCurrentVideo(channelId).then(res => res.data),
  });

  const isLoading = ytLoading || ttLoading;

  // Seed local state from REST on initial load; WS messages take over after that.
  const wsEverReceived = lastMessage !== null;

  useEffect(() => {
    if (!wsEverReceived) {
      const yt = ytQueueData || [];
      const tt = ttQueueData || [];
      const merged = [...yt, ...tt].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      setQueue(merged);
    }
  }, [ytQueueData, ttQueueData, wsEverReceived]);

  useEffect(() => {
    if (!wsEverReceived) {
      const playing =
        ytCurrentData?.current_video ||
        ttCurrentData?.current_video ||
        null;
      setCurrentVideo(playing);
    }
  }, [ytCurrentData, ttCurrentData, wsEverReceived]);

  // Handle WebSocket messages for real-time updates
  useEffect(() => {
    if (!lastMessage) return;

    // Avoid processing the same message twice
    if (lastProcessedMessage === lastMessage) return;
    setLastProcessedMessage(lastMessage);

    console.log('QueueManagement: WebSocket message received:', lastMessage.type, lastMessage);

    switch (lastMessage.type) {
      case 'initial_state':
        console.log('QueueManagement: Setting initial state from WebSocket');
        setQueue(lastMessage.queue || []);
        setCurrentVideo(lastMessage.current_content);
        break;

      case 'content_started':
        console.log('QueueManagement: Content started, updating current video');
        setCurrentVideo(lastMessage.content);
        // Remove the started content from queue if it was there
        setQueue(prevQueue => prevQueue.filter(video => video.id !== lastMessage.content.id));
        break;

      case 'content_ended':
        console.log('QueueManagement: Content ended, clearing current video');
        setCurrentVideo(null);
        break;

      case 'content_cancelled':
        console.log('QueueManagement: Content cancelled, content_id:', lastMessage.content_id);

        // Remove cancelled content from queue
        setQueue(prevQueue => prevQueue.filter(video => video.id !== lastMessage.content_id));

        // Only clear current video if the cancelled content was the currently playing one
        if (currentVideo && lastMessage.content_id === currentVideo.id) {
          console.log('QueueManagement: Clearing current video because it was cancelled');
          setCurrentVideo(null);
        }
        break;

      case 'content_added':
        console.log('QueueManagement: Content added, updating queue immediately');
        // Add new content to queue immediately if it's not currently playing
        if (lastMessage.content && lastMessage.content.status === 'pending') {
          setQueue(prevQueue => [...prevQueue, lastMessage.content]);
        } else if (lastMessage.content && lastMessage.content.status === 'playing') {
          // If it's playing, set as current video
          setCurrentVideo(lastMessage.content);
        }
        break;

      case 'queue_updated':
        console.log('QueueManagement: Queue updated from server');
        setQueue(lastMessage.queue || []);
        break;

      case 'connection_established':
        console.log('QueueManagement: WebSocket connection established, data will be synced via initial_state');
        break;

      default:
        console.log('QueueManagement: Unknown message type:', lastMessage.type);
        break;
    }
  }, [lastMessage, channelId, currentVideo]);

  // Detect platform from URL for manual add
  const isTikTokUrl = (url: string) =>
    url.includes('tiktok.com') || url.includes('vm.tiktok.com') || url.includes('vt.tiktok.com');

  const addVideoMutation = useMutation({
    mutationFn: (url: string) =>
      isTikTokUrl(url)
        ? tiktokApi.addVideo(channelId, url)
        : youtubeApi.addVideo(channelId, url),
    onSuccess: () => setAddUrl(''),
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['yt-queue', channelId] });
      queryClient.invalidateQueries({ queryKey: ['tt-queue', channelId] });
    },
  });

  const skipToNextMutation = useMutation({
    mutationFn: () => {
      // Skip the currently playing item (backend uses the active integration)
      const integration = currentVideo?.integration;
      return integration === 'tiktok'
        ? tiktokApi.skipToNext(channelId)
        : youtubeApi.skipToNext(channelId);
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['yt-queue', channelId] });
      queryClient.invalidateQueries({ queryKey: ['tt-queue', channelId] });
      queryClient.invalidateQueries({ queryKey: ['yt-current', channelId] });
      queryClient.invalidateQueries({ queryKey: ['tt-current', channelId] });
    },
  });

  const cancelVideoMutation = useMutation({
    mutationFn: ({ videoId, integration }: { videoId: number; integration?: string }) =>
      integration === 'tiktok'
        ? tiktokApi.cancelVideo(channelId, videoId)
        : youtubeApi.cancelVideo(channelId, videoId),
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['yt-queue', channelId] });
      queryClient.invalidateQueries({ queryKey: ['tt-queue', channelId] });
    },
  });

  const clearQueueMutation = useMutation({
    mutationFn: async () => {
      await Promise.all([
        youtubeApi.clearQueue(channelId),
        tiktokApi.clearQueue(channelId),
      ]);
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['yt-queue', channelId] });
      queryClient.invalidateQueries({ queryKey: ['tt-queue', channelId] });
    },
  });

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleSkipToNext = () => {
    if (window.confirm('Skip to the next video in queue?')) {
      skipToNextMutation.mutate();
    }
  };

  const handleCancelVideo = (video: Video) => {
    if (window.confirm('Cancel this video?')) {
      cancelVideoMutation.mutate({ videoId: video.id, integration: video.integration });
    }
  };

  const handleClearQueue = () => {
    if (window.confirm('Clear all pending videos from the queue?')) {
      clearQueueMutation.mutate();
    }
  };

  const handleAddVideo = () => {
    if (addUrl.trim()) {
      addVideoMutation.mutate(addUrl.trim());
    }
  };

  const pendingVideos = queue.filter(video => video.status === VideoStatus.PENDING);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
        <div style={{ width: 20, height: 20, border: '2px solid #e5e5e5', borderTopColor: '#6441a5', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 20 }}>Queue</h2>

      {/* Currently playing */}
      <div style={s.section}>
        <div style={{ ...s.row, justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Now playing</span>
          {currentVideo && (
            <button
              style={s.btn(false, false, skipToNextMutation.isPending)}
              disabled={skipToNextMutation.isPending}
              onClick={handleSkipToNext}
            >
              {skipToNextMutation.isPending ? 'Skipping…' : 'Skip'}
            </button>
          )}
        </div>
        {currentVideo ? (
          <div style={{ padding: '10px 12px', background: '#f3f0f9', borderRadius: 6, borderLeft: '3px solid #6441a5' }}>
            <div style={{ ...s.row, gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: currentVideo.integration === 'tiktok' ? '#e94560' : '#6441a5', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {currentVideo.integration === 'tiktok' ? 'TT' : 'YT'}
              </span>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>
                {currentVideo.video_title || (currentVideo.integration === 'tiktok' ? `TikTok (${currentVideo.tiktok_id})` : `YouTube (${currentVideo.youtube_id})`)}
              </div>
            </div>
            <div style={{ ...s.row, gap: 12 }}>
              <span style={s.meta}>{currentVideo.requested_by}</span>
              {currentVideo.video_duration ? <span style={s.meta}>{formatDuration(currentVideo.video_duration)}</span> : null}
              {(currentVideo.youtube_url || currentVideo.tiktok_url) && (
                <a href={currentVideo.youtube_url || currentVideo.tiktok_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#6441a5' }}>
                  ↗
                </a>
              )}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: '#bbb', padding: '10px 0' }}>Nothing playing</div>
        )}
      </div>

      {/* Add video */}
      <div style={s.section}>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Add video</div>
        <div style={s.row}>
          <input
            style={s.input}
            value={addUrl}
            onChange={e => setAddUrl(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleAddVideo()}
            placeholder="YouTube or TikTok URL"
            disabled={addVideoMutation.isPending}
          />
          <button
            style={s.btn(true, false, !addUrl.trim() || addVideoMutation.isPending)}
            disabled={!addUrl.trim() || addVideoMutation.isPending}
            onClick={handleAddVideo}
          >
            {addVideoMutation.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
        {addVideoMutation.isError && (
          <div style={{ fontSize: 12, color: '#e57373', marginTop: 6 }}>Invalid URL. Paste a YouTube or TikTok link.</div>
        )}
      </div>

      {/* Queue list */}
      <div>
        <div style={{ ...s.row, justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>
            Up next {pendingVideos.length > 0 ? `· ${pendingVideos.length}` : ''}
          </span>
          {pendingVideos.length > 0 && (
            <button
              style={s.btn(false, true, clearQueueMutation.isPending)}
              disabled={clearQueueMutation.isPending}
              onClick={handleClearQueue}
            >
              {clearQueueMutation.isPending ? 'Clearing…' : 'Clear all'}
            </button>
          )}
        </div>

        {pendingVideos.length === 0 ? (
          <div style={{ fontSize: 13, color: '#bbb', padding: '10px 0' }}>Queue is empty</div>
        ) : (
          pendingVideos.map((video, index) => (
            <div key={video.id} style={s.videoRow}>
              <span style={s.idx}>{index + 1}</span>
              <span style={{ fontSize: 10, fontWeight: 600, color: video.integration === 'tiktok' ? '#e94560' : '#6441a5', textTransform: 'uppercase', minWidth: 16 }}>
                {video.integration === 'tiktok' ? 'TT' : 'YT'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {video.video_title || (video.integration === 'tiktok' ? `TikTok (${video.tiktok_id})` : `YouTube (${video.youtube_id})`)}
                </div>
                <div style={{ ...s.row, gap: 10, marginTop: 2 }}>
                  <span style={s.meta}>{video.requested_by}</span>
                  {video.video_duration ? <span style={s.meta}>{formatDuration(video.video_duration)}</span> : null}
                  {(video.youtube_url || video.tiktok_url) && (
                    <a href={video.youtube_url || video.tiktok_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#6441a5' }}>
                      ↗
                    </a>
                  )}
                </div>
              </div>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#ccc', padding: '0 4px' }}
                onClick={() => handleCancelVideo(video)}
                disabled={cancelVideoMutation.isPending}
                title="Cancel"
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default QueueManagement;